import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { ROUTES } from 'Constants';
import { CharterPaidTag } from 'Modules/Charter/Components/CustomerCharter/CharterPaidTag';
import { CustomerCharter, customerCharterName } from 'Modules/Charter/Types/CustomerCharter';
import { formatCharterDuration } from 'Modules/Charter/Utils/formatCharterDuration';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

export interface CharterBookingCardProps {
	charter: CustomerCharter;
}

/**
 * A charter in the customer's bookings list.
 *
 * Wears the same classes as the booking cards beside it, so it sits in the list rather than looking
 * pasted into it — and says **Charter** in its own row, because the one thing the customer has to be
 * able to tell at a glance is that this is not one of their ferry bookings.
 */
export function CharterBookingCard({ charter }: CharterBookingCardProps) {
	const { startDateTime, endDateTime, ferryName, price, invoiceNumber, paymentState, isInvoiced, status } =
		charter;

	// A charter that was called off says so in place of whatever its money was doing — see
	// CharterPaidTag. Without it a cancelled charter sat in the Past tab reading **Paid**.
	const cancelled = status === 'Cancelled';

	const duration = formatCharterDuration(startDateTime, endDateTime);

	return (
		<Link
			to={ROUTES.CUSTOMER_CHARTER.url(charter.id)}
			className="booking-card-container hide-underline"
			data-test-id="customer-charter-card"
		>
			<div className="booking">
				<div className="booking-header">
					<div className="booking-header__left">
						<div className="booking-header__icon booking-header__icon--charter">
							<Icon name="sailing" useMaterialIcons />
						</div>
						<div className="booking-header__info">
							<div className="booking-header__type-row">
								<span className="booking-header__type">Charter</span>
								{stringNotEmpty(invoiceNumber) && (
									<span className="booking-header__id booking-header__id--mobile">
										#{invoiceNumber}
									</span>
								)}
							</div>
							<div className="booking-header__title-row">
								<h4 className="booking-header__title">{customerCharterName(charter)}</h4>
								<div className="booking-header__status--mobile">
									<CharterPaidTag state={paymentState} invoiced={isInvoiced} cancelled={cancelled} />
								</div>
							</div>
							<div className="booking-header__meta">
								<span>
									<Icon name="calendar_today" useMaterialIcons />
									{dayjs(startDateTime).format('DD MMM YYYY')}, {formatStandardTime(startDateTime)}
								</span>
								<Icon name="arrow_forward" useMaterialIcons classname="booking-header__arrow" />
								<span>
									<Icon name="calendar_today" useMaterialIcons />
									{dayjs(endDateTime).format('DD MMM YYYY')}, {formatStandardTime(endDateTime)}
								</span>
							</div>
						</div>
					</div>
					<div className="booking-header__right">
						{stringNotEmpty(invoiceNumber) && (
							<span className="booking-header__id booking-header__id--desktop">#{invoiceNumber}</span>
						)}
						<div className="tags__container">
							<CharterPaidTag state={paymentState} invoiced={isInvoiced} cancelled={cancelled} />
						</div>
						<span className="booking-header__price" data-test-id="customer-charter-card-price">
							{price !== undefined && price !== null ? formatPriceDisplay(price, false) : ''}
						</span>
					</div>
				</div>
				<div className="ticket-body">
					<div className="ticket-body__item">
						<p className="ticket-body__label">Ferry</p>
						<p className="ticket-body__value">{ferryName}</p>
					</div>
					{stringNotEmpty(duration) && (
						<div className="ticket-body__item">
							<p className="ticket-body__label">Ferry held for</p>
							<p className="ticket-body__value">{duration}</p>
						</div>
					)}
				</div>
			</div>
			<span className="view-booking-detail-section__edit-button">View charter</span>
		</Link>
	);
}
